import React from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ShareIcon from '@mui/icons-material/Share';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import SearchOffIcon from '@mui/icons-material/SearchOff';

type EmptyStateType = 'default' | 'files' | 'favorites' | 'shared' | 'trash' | 'search' | 'error';

interface EmptyStateProps {
  type?: EmptyStateType;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  actionText?: string;
  onAction?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ 
  type = 'default',
  title,
  description,
  icon,
  actionText,
  onAction
}) => {
  const getEmptyStateContent = () => {
    switch (type) {
      case 'files':
        return {
          icon: icon || <FolderOpenIcon sx={{ fontSize: 70 }} />,
          title: title || 'No files here',
          description: description || 'Upload files or create folders to get started'
        };
      case 'favorites':
        return {
          icon: icon || <StarBorderIcon sx={{ fontSize: 70 }} />,
          title: title || 'No favorites yet',
          description: description || 'Star files and folders to add them to your favorites for quick access'
        };
      case 'shared':
        return {
          icon: icon || <ShareIcon sx={{ fontSize: 70 }} />,
          title: title || 'Nothing shared with you',
          description: description || 'Files and folders shared with you will appear here'
        };
      case 'trash':
        return {
          icon: icon || <DeleteOutlineIcon sx={{ fontSize: 70 }} />,
          title: title || 'Your trash is empty',
          description: description || 'Files you delete will appear here for 30 days before being permanently removed'
        };
      case 'search':
        return {
          icon: icon || <SearchOffIcon sx={{ fontSize: 70 }} />,
          title: title || 'No search results',
          description: description || 'Try different keywords or check your filters'
        };
      case 'error':
        return {
          icon: icon || <CloudOffIcon sx={{ fontSize: 70 }} />,
          title: title || 'Something went wrong',
          description: description || 'We couldn\'t load your content. Please try again later.'
        };
      default:
        return {
          icon: icon || <InsertDriveFileOutlinedIcon sx={{ fontSize: 70 }} />,
          title: title || 'No content yet',
          description: description || 'Content will appear here once available'
        };
    }
  };

  const emptyStateContent = getEmptyStateContent();

  return (
    <Paper 
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 6,
        minHeight: 300,
        backgroundColor: 'background.default',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
          backgroundColor: 'background.paper',
          borderRadius: '50%',
          color: 'text.secondary',
          boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
          mb: 3
        }}
      >
        {emptyStateContent.icon}
      </Box>
      <Typography
        variant="h5"
        component="h2"
        sx={{ mb: 1, fontWeight: 600 }}
      >
        {emptyStateContent.title}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 400, mb: 3 }}
      >
        {emptyStateContent.description}
      </Typography>
      {actionText && onAction && (
        <Button 
          variant="contained" 
          color="primary" 
          onClick={onAction}
        >
          {actionText}
        </Button>
      )}
    </Paper>
  );
};

export default EmptyState; 